import React, { useState } from 'react';
import { ConceptStatusPill, CategoryChip, BasisChip, Chevron, ProgressBar, Btn, money, fmtLocal, fmtWhen } from './ui';

function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  if (!children) return null;
  return (
    <div className="border-t border-[#F2F2F2]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="pe-toggle w-full flex items-center justify-between bg-white transition-colors duration-200"
        style={{ padding: '9px 12px' }}
      >
        <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.05em' }}>{label}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="border-t border-[#F2F2F2]" style={{ padding: '10px 12px', fontSize: 12, lineHeight: 1.6, textWrap: 'pretty' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Cell({ k, v, full = false, children }) {
  return (
    <div className="bg-white flex items-center justify-between gap-2" style={{ padding: '8px 12px', gridColumn: full ? '1 / -1' : undefined, minWidth: 0 }}>
      <span className="uppercase whitespace-nowrap" style={{ fontSize: 11, color: '#6B7280', letterSpacing: '0.05em' }}>{k}</span>
      {children || <span className="font-bold" style={{ fontSize: 11 }}>{v}</span>}
    </div>
  );
}

function Poster({ concept, onRegenerate, busy }) {
  const base = { width: '30%', maxWidth: 180, minWidth: 96, flex: 'none', minHeight: 240 };
  if (concept.posterUrl) {
    return (
      <div className="border-r-2 border-black" style={{ ...base, background: '#000' }}>
        <img src={concept.posterUrl} alt="" className="w-full h-full object-cover block" style={{ minHeight: 240 }} />
      </div>
    );
  }
  if (concept.posterPending) {
    return (
      <div className="border-r-2 border-black flex flex-col items-center justify-center gap-3" style={{ ...base, background: '#F5F5F5', padding: 10 }}>
        <span className="uppercase text-center" style={{ fontSize: 9, color: '#6B7280', letterSpacing: '0.1em' }}>RENDERING POSTER…</span>
        <ProgressBar percent={45} height={8} pulse className="w-[70%]" />
      </div>
    );
  }
  return (
    <div className="border-r-2 border-black flex flex-col items-center justify-center gap-3" style={{ ...base, background: '#000', padding: 10 }}>
      <span className="uppercase font-bold text-white" style={{ fontSize: 10, letterSpacing: '0.1em' }}>NO POSTER</span>
      {onRegenerate && (
        <button
          type="button"
          disabled={busy}
          onClick={onRegenerate}
          className="font-mono font-bold uppercase text-white border-2 border-white bg-transparent hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50"
          style={{ fontSize: 10, padding: '6px 10px', letterSpacing: '0.05em' }}
        >
          REGENERATE
        </button>
      )}
    </div>
  );
}

/**
 * One concept. `onAction` receives (action, concept).
 * Actions: approve | unapprove | reject | edit | retry | regenerate-poster | view-event | revoke-claim
 * Gates (absent, not disabled): PREVIEW / VIEW EVENT always; APPROVE UNAPPROVE REJECT RESTORE
 * RETRY EDIT REGENERATE need `canEdit` (`party_engine` · edit); REVOKE CLAIM needs `canPublish`.
 */
export default function ConceptCard({ concept, onAction, busy = false, canEdit = false, canPublish = false }) {
  const c = concept;
  const published = c.status === 'published';
  const claimed = published && c.isClaimed;
  const venueLabel = c.venueCostBasis === 'none' ? 'No venue' : (c.venueName || (c.venueKind ? `a ${c.venueKind}` : 'No venue'));
  const liveStatus = published && c.eventStatus
    ? `${c.eventStatus.replace('_', ' ').toUpperCase()} · ${money(c.eventPledgedCents).replace(/\.00$/, '')} / ${money(c.fundingGoalCents).replace(/\.00$/, '')}`
    : null;

  return (
    <div className="rounded-[24px] bg-white font-mono flex h-full overflow-hidden" style={{ minWidth: 0 }}>
      <Poster concept={c} busy={busy} onRegenerate={!canEdit || c.status === 'rejected' ? null : () => onAction('regenerate-poster', c)} />
      <div className="flex-1 flex flex-col" style={{ minWidth: 0 }}>
        <div style={{ padding: '14px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold" style={{ fontSize: 18, lineHeight: 1.3, textWrap: 'pretty', margin: 0 }}>{c.title}</h3>
            <div className="flex flex-col items-end gap-1 flex-none">
              <ConceptStatusPill status={c.status} />
              {liveStatus && <span className="uppercase whitespace-nowrap" style={{ fontSize: 10 }}>{liveStatus}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(c.categories || []).map((cat, i) => <CategoryChip key={cat} label={cat} index={i} />)}
          </div>
          <div className="uppercase" style={{ fontSize: 12, letterSpacing: '0.05em' }}>
            {fmtLocal(c.startsAtLocal)}<span style={{ color: '#9CA3AF' }}> · </span>{c.durationHours} HRS
          </div>
        </div>

        <div className="border-t border-[#F2F2F2]" style={{ background: '#000', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Cell k="TICKET" v={money(c.ticketPriceCents)} />
          <Cell k="MIN GUESTS" v={c.minGuests} />
          <Cell k="GOAL" v={money(c.fundingGoalCents)} />
          <Cell k="CAPACITY" v={c.maxAttendees} />
          <Cell k="VENUE" full>
            <span className="flex items-center gap-2 min-w-0">
              <span className="font-bold truncate" style={{ fontSize: 11 }}>{venueLabel}</span>
              <BasisChip basis={c.venueCostBasis} href={c.venueEvidenceUrl} />
            </span>
          </Cell>
          <Cell k="VENUE RENTAL" v={`≈ ${money(c.venueCostEstimateCents || 0)}`} />
          <Cell k="PER HEAD" v={`≈ ${money(c.perHeadCostCents)}`} />
          <Cell k="HOST MARGIN AT GOAL" v={money(c.hostMarginCentsAtGoal)} full />
        </div>

        <Collapsible label="WHY THIS PARTY">{c.rationale}</Collapsible>
        <Collapsible label="HOST BRIEF">{c.hostBrief}</Collapsible>

        {c.status === 'publish_failed' && c.publishError && (
          <div className="border-t border-[#F2F2F2] uppercase" style={{ padding: '10px 12px', fontSize: 11, color: '#DC2626' }}>
            PUBLISH FAILED — {c.publishError}
          </div>
        )}

        {published && (
          <div className="border-t border-[#F2F2F2] flex items-center justify-between gap-2" style={{ padding: '10px 12px' }}>
            <span className="font-bold uppercase" style={{ fontSize: 11 }}>
              {claimed
                ? `CLAIMED BY @${(c.claimedByHandle || c.claimedByDisplayName || 'host').toUpperCase()}${c.claimedAt ? ` · ${fmtWhen(c.claimedAt)}` : ''}`
                : `UNCLAIMED · ${c.hostsInvited} INVITED`}
            </span>
            {claimed && canPublish && (
              <Btn variant="dangerText" size="sm" disabled={busy} onClick={() => onAction('revoke-claim', c)} style={{ fontSize: 11, padding: 0, color: '#DC2626' }}>
                REVOKE CLAIM
              </Btn>
            )}
          </div>
        )}

        <div className="mt-auto border-t border-[#F2F2F2] flex flex-wrap gap-2" style={{ padding: '10px 12px' }}>
          {!published && (
            <Btn variant="secondary" size="sm" onClick={() => onAction('preview', c)}>PREVIEW</Btn>
          )}
          {canEdit && c.status === 'draft' && (
            <>
              <Btn variant="primary" size="sm" disabled={busy} onClick={() => onAction('approve', c)}>APPROVE</Btn>
              <Btn variant="secondary" size="sm" disabled={busy} onClick={() => onAction('edit', c)}>EDIT</Btn>
              <Btn variant="dangerText" size="sm" disabled={busy} onClick={() => onAction('reject', c)} style={{ fontSize: 11, padding: '8px 4px', color: '#DC2626', textDecoration: 'none' }}>REJECT</Btn>
            </>
          )}
          {canEdit && c.status === 'approved' && (
            <>
              <Btn variant="secondary" size="sm" disabled={busy} onClick={() => onAction('unapprove', c)}>UNAPPROVE</Btn>
              <Btn variant="secondary" size="sm" disabled={busy} onClick={() => onAction('edit', c)}>EDIT</Btn>
            </>
          )}
          {canEdit && c.status === 'rejected' && (
            <Btn variant="secondary" size="sm" disabled={busy} onClick={() => onAction('approve', c)}>RESTORE</Btn>
          )}
          {published && (
            <Btn variant="secondary" size="sm" onClick={() => onAction('view-event', c)}>VIEW EVENT →</Btn>
          )}
          {canEdit && c.status === 'publish_failed' && (
            <>
              <Btn variant="primary" size="sm" disabled={busy} onClick={() => onAction('retry', c)}>RETRY</Btn>
              <Btn variant="secondary" size="sm" disabled={busy} onClick={() => onAction('edit', c)}>EDIT</Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Dashed placeholder for a concept not yet generated — the only dashed border in the system. */
export function ConceptPlaceholder({ index, total, label = 'GENERATING CONCEPTS…' }) {
  return (
    <div className="bg-white font-mono flex flex-col items-center justify-center gap-2" style={{ border: '2px dashed #9CA3AF', minHeight: 220 }}>
      <span className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.1em', color: '#6B7280' }}>CONCEPT {index} OF {total}</span>
      <span className="uppercase" style={{ fontSize: 11, color: '#9CA3AF', letterSpacing: '0.05em' }}>{label}</span>
    </div>
  );
}
